/**
 * Utility functions for managing Absolutely video progress in localStorage
 * These functions handle current session progress tracking
 */

/**
 * Save video progress to localStorage (for current session tracking)
 * @param {number} chapterId - Chapter ID
 * @param {string} stepId - Step ID
 * @param {number} videoProgress - Progress in seconds
 */
export const saveVideoProgressToStorage = (chapterId, stepId, videoProgress) => {
  if (!chapterId || !stepId) {
    return;
  }
  try {
    const key = `absolutely_video_progress_${chapterId}_${stepId}`;
    // Always overwrite with new progress (even if previously marked as 'completed')
    // This allows progress tracking to work when user watches again after completion
    localStorage.setItem(key, String(videoProgress));
  } catch (error) {
    console.warn('[saveVideoProgressToStorage] Failed to save to localStorage:', error);
  }
};

/**
 * Load video progress from localStorage (for current session tracking)
 * @param {number} chapterId - Chapter ID
 * @param {string} stepId - Step ID
 * @returns {number|null} - Progress in seconds, or null if not found/completed
 */
export const loadVideoProgressFromStorage = (chapterId, stepId) => {
  if (!chapterId || !stepId) {
    return null;
  }
  try {
    const key = `absolutely_video_progress_${chapterId}_${stepId}`;
    const value = localStorage.getItem(key);

    // Return null if value is null, empty, or marked as completed
    if (!value || value === 'completed') {
      return null;
    }

    const progressSeconds = parseFloat(value);
    // Treat 0 as "no progress" (same as null)
    if (isNaN(progressSeconds) || progressSeconds === 0) {
      return null;
    }
    return progressSeconds;
  } catch (error) {
    console.warn('[loadVideoProgressFromStorage] Failed to load from localStorage:', error);
    return null;
  }
};

/**
 * Mark video progress as completed in localStorage
 * @param {number} chapterId - Chapter ID
 * @param {string} stepId - Step ID
 */
export const markVideoProgressAsCompleted = (chapterId, stepId) => {
  if (!chapterId || !stepId) {
    return;
  }
  try {
    const key = `absolutely_video_progress_${chapterId}_${stepId}`;
    localStorage.setItem(key, 'completed');
  } catch (error) {
    console.warn(
      '[markVideoProgressAsCompleted] Failed to mark as completed in localStorage:',
      error,
    );
  }
};

/**
 * Check if video progress is marked as completed in localStorage
 * @param {number} chapterId - Chapter ID
 * @param {string} stepId - Step ID
 * @returns {boolean} - True if marked as completed
 */
export const isVideoProgressCompleted = (chapterId, stepId) => {
  if (!chapterId || !stepId) {
    return false;
  }
  try {
    const key = `absolutely_video_progress_${chapterId}_${stepId}`;
    const value = localStorage.getItem(key);
    return value === 'completed';
  } catch (error) {
    console.warn('[isVideoProgressCompleted] Failed to check completion status:', error);
    return false;
  }
};

/**
 * Clear video progress from localStorage (for current session)
 * @param {number} chapterId - Chapter ID
 * @param {string} stepId - Step ID
 */
export const clearVideoProgressFromStorage = (chapterId, stepId) => {
  if (!chapterId || !stepId) {
    return;
  }
  try {
    const key = `absolutely_video_progress_${chapterId}_${stepId}`;
    localStorage.removeItem(key);
  } catch (error) {
    console.warn('[clearVideoProgressFromStorage] Failed to clear from localStorage:', error);
  }
};
